import { useEffect, useRef } from "react";
import type { MutableRefObject } from "react";

import { useUserImageStore } from "../../store/user-image.store.js";

interface UseDesktopPhotoSyncProps {
  occupantIds: string[];
  selfUserId: string;
  onPhotoUpdated: (payload: {
    userId: string;
    pictureUrl: string | null;
  }) => void;
}

interface CachedPhotoEntry {
  pictureUrl: string;
}

interface PhotoSyncRefs {
  pictureUrlCacheRef: MutableRefObject<Map<string, string>>;
  photoCacheMetaRef: MutableRefObject<Map<string, CachedPhotoEntry>>;
}

const noopPhotoUpdated = () => {
  // no-op
};

const resolveRelevantIds = (
  occupantIds: string[],
  selfUserId: string
): string[] => {
  const ids = [...occupantIds];

  if (!ids.includes(selfUserId)) {
    ids.push(selfUserId);
  }

  return ids;
};

const removeCachedPhoto = ({
  userId,
  refs,
  onPhotoUpdated,
  sendClearUpdate,
}: {
  userId: string;
  refs: PhotoSyncRefs;
  onPhotoUpdated: (payload: {
    userId: string;
    pictureUrl: string | null;
  }) => void;
  sendClearUpdate?: boolean;
}) => {
  refs.photoCacheMetaRef.current.delete(userId);
  refs.pictureUrlCacheRef.current.delete(userId);

  if (sendClearUpdate) {
    onPhotoUpdated({ pictureUrl: null, userId });
  }
};

const syncSingleUserPhoto = ({
  userId,
  refs,
  onPhotoUpdated,
}: {
  userId: string;
  refs: PhotoSyncRefs;
  onPhotoUpdated: (payload: {
    userId: string;
    pictureUrl: string | null;
  }) => void;
}) => {
  const store = useUserImageStore.getState();
  const pictureUrl = store.getPhoto(userId);
  const photoStatus = store.getPhotoStatus(userId);

  if (!pictureUrl) {
    const shouldClearDesktopPhoto =
      photoStatus === "missing" &&
      (refs.photoCacheMetaRef.current.has(userId) ||
        refs.pictureUrlCacheRef.current.has(userId));

    if (shouldClearDesktopPhoto) {
      removeCachedPhoto({
        onPhotoUpdated,
        refs,
        sendClearUpdate: true,
        userId,
      });
    }
    return;
  }

  const cachedEntry = refs.photoCacheMetaRef.current.get(userId);
  if (cachedEntry?.pictureUrl === pictureUrl) {
    return;
  }

  const previousPictureUrl = refs.pictureUrlCacheRef.current.get(userId);
  refs.photoCacheMetaRef.current.set(userId, { pictureUrl });
  refs.pictureUrlCacheRef.current.set(userId, pictureUrl);

  if (previousPictureUrl !== pictureUrl) {
    onPhotoUpdated({ pictureUrl, userId });
  }
};

const removeIrrelevantCachedUsers = ({
  relevantUserIds,
  refs,
}: {
  relevantUserIds: Set<string>;
  refs: PhotoSyncRefs;
}) => {
  for (const cachedUserId of [...refs.photoCacheMetaRef.current.keys()]) {
    if (relevantUserIds.has(cachedUserId)) {
      continue;
    }

    removeCachedPhoto({
      onPhotoUpdated: noopPhotoUpdated,
      refs,
      userId: cachedUserId,
    });
  }
};

export function useDesktopPhotoSync({
  occupantIds,
  selfUserId,
  onPhotoUpdated,
}: UseDesktopPhotoSyncProps) {
  const pictureUrlCacheRef = useRef(new Map<string, string>());
  const photoCacheMetaRef = useRef(new Map<string, CachedPhotoEntry>());

  useEffect(() => {
    const refs: PhotoSyncRefs = {
      photoCacheMetaRef,
      pictureUrlCacheRef,
    };

    let cancelled = false;
    let syncQueue = Promise.resolve();

    const syncPhotos = () => {
      if (cancelled) {
        return;
      }

      const relevantUserIds = resolveRelevantIds(occupantIds, selfUserId);
      const relevantUserIdSet = new Set(relevantUserIds);

      for (const userId of relevantUserIds) {
        syncSingleUserPhoto({
          onPhotoUpdated,
          refs,
          userId,
        });
      }

      removeIrrelevantCachedUsers({
        refs,
        relevantUserIds: relevantUserIdSet,
      });
    };

    const queuePhotoSync = () => {
      syncQueue = syncQueue.then(syncPhotos).catch(() => {
        // Ignore queue failures and allow future sync events.
      });
    };

    queuePhotoSync();
    const unsubscribe = useUserImageStore.subscribe(queuePhotoSync);

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [occupantIds, selfUserId, onPhotoUpdated]);

  return { pictureUrlCacheRef };
}
